import { c as _c } from "react/compiler-runtime";
import { useTranslations } from "next-intl";
import { DialogTitle } from "@repo/ui/dialog";
import { Icon } from "@repo/ui/icon";
import { cn } from "@repo/ui/utils";
import { RichTextBodyCopyWithLinks } from "@/components/common/rich-text/rich-text-body-copy-with-links.component";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import { resolvePanelContent } from "./commercial-tenant-card.helpers";
import type { CommercialTenantDetail } from "./commercial-tenant-card.types";
interface CommercialTenantDetailPanelProps extends CommercialTenantDetail {
  /** Tenant name shown as the panel headline. */
  title: string;
}
export function CommercialTenantDetailPanel(t0) {
  const $ = _c(35);
  const {
    title,
    address,
    tradingHours,
    bodyCopy,
    website,
    email,
    phone,
    facebook,
    instagram
  } = t0;
  const t = useTranslations("commercialTenantCard");
  let t1;
  if ($[0] !== address || $[1] !== bodyCopy || $[2] !== email || $[3] !== facebook || $[4] !== instagram || $[5] !== phone || $[6] !== tradingHours || $[7] !== website) {
    t1 = resolvePanelContent({
      address,
      tradingHours,
      bodyCopy,
      website,
      email,
      phone,
      facebook,
      instagram
    });
    $[0] = address;
    $[1] = bodyCopy;
    $[2] = email;
    $[3] = facebook;
    $[4] = instagram;
    $[5] = phone;
    $[6] = tradingHours;
    $[7] = website;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const {
    whatsappHref,
    emailHref,
    websiteHref,
    facebookHref,
    instagramHref,
    hasAddress,
    hasTradingHours,
    hasBodyCopy,
    showAddressRow,
    hasContactIcons
  } = t1;
  let t2;
  if ($[9] !== title) {
    t2 = <DialogTitle className="typography-desktop-heading-subtitle-1 text-text-primary">{title}</DialogTitle>;
    $[9] = title;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== address || $[12] !== hasAddress || $[13] !== hasTradingHours || $[14] !== showAddressRow || $[15] !== t || $[16] !== tradingHours) {
    t3 = showAddressRow && <div className="flex flex-col gap-6 desktop:flex-row desktop:gap-6">{hasAddress && address && <div className="flex flex-1 flex-col gap-3"><p className="typography-component-sm-medium text-text-secondary">{t("addressLabel")}</p><RichTextBodyCopyWithLinks richText={address} as="body-sm-medium-flat" className="text-text-primary" /></div>}{hasTradingHours && tradingHours && <div className="flex flex-1 flex-col gap-3"><p className="typography-component-sm-medium text-text-secondary">{t("tradingHoursLabel")}</p><RichTextBodyCopyWithLinks richText={tradingHours} as="body-sm-medium-flat" className="text-text-primary" /></div>}</div>;
    $[11] = address;
    $[12] = hasAddress;
    $[13] = hasTradingHours;
    $[14] = showAddressRow;
    $[15] = t;
    $[16] = tradingHours;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== bodyCopy || $[19] !== hasBodyCopy) {
    t4 = hasBodyCopy && bodyCopy && <RichTextCondensed richText={bodyCopy} as="body-tablet" className="text-text-secondary" />;
    $[18] = bodyCopy;
    $[19] = hasBodyCopy;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== emailHref || $[22] !== facebookHref || $[23] !== hasContactIcons || $[24] !== instagramHref || $[25] !== t || $[26] !== title || $[27] !== websiteHref || $[28] !== whatsappHref) {
    t5 = hasContactIcons && <div className="flex items-center gap-[13px]">{whatsappHref && <ContactIconLink href={whatsappHref} icon="whatsapp" label={t("whatsappLabel", {
        name: title
      })} />}{emailHref && <ContactIconLink href={emailHref} icon="mail" label={t("emailLabel", {
        name: title
      })} />}{websiteHref && <ContactIconLink href={websiteHref} icon="globe" label={t("websiteLabel", {
        name: title
      })} external={true} />}{facebookHref && <ContactIconLink href={facebookHref} icon="facebook" label={t("facebookLabel", {
        name: title
      })} external={true} />}{instagramHref && <ContactIconLink href={instagramHref} icon="instagram" label={t("instagramLabel", {
        name: title
      })} external={true} />}</div>;
    $[21] = emailHref;
    $[22] = facebookHref;
    $[23] = hasContactIcons;
    $[24] = instagramHref;
    $[25] = t;
    $[26] = title;
    $[27] = websiteHref;
    $[28] = whatsappHref;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== t2 || $[31] !== t3 || $[32] !== t4 || $[33] !== t5) {
    t6 = <div className="flex flex-col gap-6 overflow-y-auto px-4 py-8 desktop:gap-8 desktop:p-16" data-testid="commercial-tenant-detail-panel">{t2}{t3}{t4}{t5}</div>;
    $[30] = t2;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  return t6;
}
function ContactIconLink(t0) {
  const $ = _c(10);
  const {
    href,
    icon,
    label,
    external
  } = t0;
  let t1;
  if ($[0] !== external) {
    t1 = external ? {
      target: "_blank",
      rel: "noopener noreferrer"
    } : {};
    $[0] = external;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex size-8 items-center justify-center rounded-full bg-surface-base-secondary text-text-primary", "transition-colors hover:text-text-secondary");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== icon) {
    t3 = <Icon name={icon} className="size-4" />;
    $[3] = icon;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== href || $[6] !== label || $[7] !== t1 || $[8] !== t3) {
    t4 = <a href={href} aria-label={label} {...t1} className={t2}>{t3}</a>;
    $[5] = href;
    $[6] = label;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}